import PropTypes from "prop-types";

/**
 * PropTypes for ChargingProfileSidebar component.
 *
 * @property {Object|null} selectedProfile - Currently selected charging profile object
 * @property {Object} filters - Current filter state
 * @property {Function} onFilterChange - Filter change dispatcher
 * @property {number} visibleCount - Number of profiles after filtering
 * @property {Array<Object>} chargePoints - All charge points for filter dropdown
 * @property {Function} [onClearProfile] - Clear profile handler
 * @property {boolean} [canEdit=false] - Whether actions are enabled
 * @property {Function} [onError] - Error callback
 */
export const chargingProfileSidebarPropTypes = {
  selectedProfile: PropTypes.object,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  visibleCount: PropTypes.number.isRequired,
  chargePoints: PropTypes.array.isRequired,
  onClearProfile: PropTypes.func,
  canEdit: PropTypes.bool,
  onError: PropTypes.func,
};

/**
 * PropTypes for ChargingProfileFormModal component.
 *
 * @property {boolean} isOpen - Whether modal is visible
 * @property {Function} onClose - Callback to close modal
 * @property {Function} onSave - Callback to save charging profile
 * @property {Array<Object>} [chargePoints] - Available charge points for selection
 * @property {Function} [onRequestChargePointDetails] - Optional callback to load charge point connectors
 */
export const chargingProfileFormModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  chargePoints: PropTypes.array,
  onRequestChargePointDetails: PropTypes.func,
};

/**
 * PropTypes for SchedulePeriodEditor component.
 *
 * @property {Array<Object>} periods - Array of period objects
 * @property {Function} onChange - Callback when periods change
 * @property {string} [rateUnit="A"] - Rate unit ("A" or "W") for display
 */
export const schedulePeriodEditorPropTypes = {
  periods: PropTypes.arrayOf(
    PropTypes.shape({
      startPeriod: PropTypes.number,
      limit: PropTypes.number,
      numberPhases: PropTypes.number,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  rateUnit: PropTypes.oneOf(["A", "W"]),
};

/**
 * PropTypes for CompositeScheduleViewer component.
 *
 * @property {string} chargePointId - Charge Point ID to fetch schedule for
 * @property {number} [connectorId=0] - Connector ID (0 for charge point level)
 * @property {Function} [onError] - Callback when fetch fails
 */
export const compositeScheduleViewerPropTypes = {
  chargePointId: PropTypes.string,
  connectorId: PropTypes.number,
  onError: PropTypes.func,
};

/**
 * PropTypes for SmartCharging view.
 * No external props are expected; kept for consistency across view definitions.
 */
export const smartChargingViewPropTypes = {};
